// /admin/tienda — Manejo de la web (self-service).
//
// Permite a Nico/Roberto editar el contenido editorial de la tienda sin tocar código:
// - Hero (eyebrow, titular, sub, CTA)
// - Categorías visibles y orden + count por categoría
// - Bloque empresas (titular, sub, CTA, meta)
// - Promo bar (3 mensajes rotativos)
// - Toggle Essential/Premium default
// - Versión de la tienda (V1 / V2)
//
// Storage: tabla app_config con keys tienda_*. Policy RLS app_config_public_read
// permite a anon/authenticated leer estas keys (tienda los consume en runtime).

import { useEffect, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { invalidarCacheTiendaConfig } from '../../lib/useTiendaConfig'

const NAVY = '#1A3461'
const TEAL = '#00A8B5'
const LINE = '#E2E8F0'

export default function AdminTienda() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [categoriasDisponibles, setCategoriasDisponibles] = useState([])

  // Valores editables
  const [version, setVersion] = useState('v1')
  const [heroEyebrow, setHeroEyebrow] = useState('')
  const [heroTitulo, setHeroTitulo] = useState('')
  const [heroSubtitulo, setHeroSubtitulo] = useState('')
  const [heroCta, setHeroCta] = useState('')
  const [categoriasConfig, setCategoriasConfig] = useState([])
  const [tierDefault, setTierDefault] = useState('essential')
  const [empTitulo, setEmpTitulo] = useState('')
  const [empSubtitulo, setEmpSubtitulo] = useState('')
  const [empCta, setEmpCta] = useState('')
  const [empMeta, setEmpMeta] = useState('')
  const [promoBar, setPromoBar] = useState([])

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setMsg(null)
    try {
      // 1) Config actual
      const { data: rows, error } = await supabase.from('app_config').select('key,value').or('key.like.tienda_%,key.eq.tienda_version')
      if (error) throw error
      const map = new Map((rows || []).map(r => [r.key, r.value]))
      setVersion(map.get('tienda_version') || 'v1')
      setHeroEyebrow(map.get('tienda_hero_eyebrow') || '')
      setHeroTitulo(map.get('tienda_hero_titulo') || '')
      setHeroSubtitulo(map.get('tienda_hero_subtitulo') || '')
      setHeroCta(map.get('tienda_hero_cta_label') || '')
      setTierDefault(map.get('tienda_kits_tier_default') || 'essential')
      setEmpTitulo(map.get('tienda_empresas_titulo') || '')
      setEmpSubtitulo(map.get('tienda_empresas_subtitulo') || '')
      setEmpCta(map.get('tienda_empresas_cta_label') || '')
      setEmpMeta(map.get('tienda_empresas_meta') || '')
      try { setCategoriasConfig(JSON.parse(map.get('tienda_categorias_config') || '[]')) } catch { setCategoriasConfig([]) }
      try { setPromoBar(JSON.parse(map.get('tienda_promo_bar') || '[]')) } catch { setPromoBar([]) }

      // 2) Categorías disponibles en DB con count actual
      const { data: cats } = await supabase.rpc('tienda_get_productos')
      const counts = new Map()
      ;(cats || []).forEach(p => {
        const c = (p.categoria || '').toLowerCase()
        counts.set(c, (counts.get(c) || 0) + 1)
      })
      setCategoriasDisponibles([...counts.entries()].map(([slug, n]) => ({ slug, productos_en_db: n })))
    } catch (e) {
      setMsg({ tipo: 'error', texto: `Error cargando config: ${e.message}` })
    } finally {
      setLoading(false)
    }
  }

  async function guardarKey(key, value) {
    const { error } = await supabase.from('app_config').update({ value, updated_at: new Date().toISOString() }).eq('key', key)
    if (error) throw new Error(`${key}: ${error.message}`)
  }

  async function guardar() {
    setSaving(true)
    setMsg(null)
    try {
      await Promise.all([
        guardarKey('tienda_version', version),
        guardarKey('tienda_hero_eyebrow', heroEyebrow),
        guardarKey('tienda_hero_titulo', heroTitulo),
        guardarKey('tienda_hero_subtitulo', heroSubtitulo),
        guardarKey('tienda_hero_cta_label', heroCta),
        guardarKey('tienda_kits_tier_default', tierDefault),
        guardarKey('tienda_empresas_titulo', empTitulo),
        guardarKey('tienda_empresas_subtitulo', empSubtitulo),
        guardarKey('tienda_empresas_cta_label', empCta),
        guardarKey('tienda_empresas_meta', empMeta),
        guardarKey('tienda_categorias_config', JSON.stringify(categoriasConfig)),
        guardarKey('tienda_promo_bar', JSON.stringify(promoBar)),
      ])
      invalidarCacheTiendaConfig()
      setMsg({ tipo: 'ok', texto: '✓ Cambios guardados. Los clientes ven los cambios al refrescar la tienda (cache 60 seg).' })
      setTimeout(() => setMsg(null), 6000)
    } catch (e) {
      setMsg({ tipo: 'error', texto: `Error: ${e.message}` })
    } finally {
      setSaving(false)
    }
  }

  // Helpers para categorías
  function actualizarCategoria(i, campo, valor) {
    setCategoriasConfig(prev => prev.map((c, ix) => ix === i ? { ...c, [campo]: valor } : c))
  }
  function moverCategoria(i, delta) {
    setCategoriasConfig(prev => {
      const cp = [...prev]
      const j = i + delta
      if (j < 0 || j >= cp.length) return cp
      ;[cp[i], cp[j]] = [cp[j], cp[i]]
      return cp.map((c, idx) => ({ ...c, orden: idx + 1 }))
    })
  }
  function agregarCategoriaFaltante(slug) {
    setCategoriasConfig(prev => [...prev, { slug, label: slug, visible: true, orden: prev.length + 1, count: 6 }])
  }
  function quitarCategoria(i) {
    setCategoriasConfig(prev => prev.filter((_, ix) => ix !== i).map((c, idx) => ({ ...c, orden: idx + 1 })))
  }

  function actualizarPromo(i, campo, valor) {
    setPromoBar(prev => prev.map((m, ix) => ix === i ? { ...m, [campo]: valor } : m))
  }
  function agregarPromo() {
    setPromoBar(prev => [...prev, { texto: '', visible: true }])
  }
  function quitarPromo(i) {
    setPromoBar(prev => prev.filter((_, ix) => ix !== i))
  }

  // Slugs en DB que NO están en categoriasConfig (para botón "agregar")
  const slugsEnConfig = new Set(categoriasConfig.map(c => c.slug))
  const faltantes = categoriasDisponibles.filter(c => !slugsEnConfig.has(c.slug))

  if (loading) {
    return <AdminLayout><div className="page"><h1>Manejo de la web</h1><div className="card">Cargando…</div></div></AdminLayout>
  }

  return (
    <AdminLayout>
      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
          <div>
            <h1>Manejo de la web</h1>
            <p className="lead" style={{ margin: 0 }}>Editá el contenido editorial de la tienda sin código.</p>
          </div>
          <button
            type="button"
            onClick={guardar}
            disabled={saving}
            style={{
              background: saving ? '#94a3b8' : NAVY,
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '12px 20px', fontSize: 13, fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >{saving ? '⏳ Guardando…' : '💾 Guardar cambios'}</button>
        </div>

        {msg && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
            background: msg.tipo === 'ok' ? '#ECFDF5' : '#FEF2F2',
            color: msg.tipo === 'ok' ? '#047857' : '#991B1B',
            border: `1px solid ${msg.tipo === 'ok' ? '#6EE7B7' : '#FCA5A5'}`,
          }}>{msg.texto}</div>
        )}

        {/* Versión */}
        <Section titulo="Versión de la tienda" sub="V1 = clásica, V2 = rediseño Blueland. Cambio al instante, sin redeploy.">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="radio" name="version" checked={version === 'v1'} onChange={() => setVersion('v1')} /> V1 (clásica)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="radio" name="version" checked={version === 'v2'} onChange={() => setVersion('v2')} /> V2 (rediseño Blueland)
            </label>
          </div>
        </Section>

        {/* Hero */}
        <Section titulo="Hero" sub="Lo primero que ve el cliente al entrar a la tienda.">
          <Input label="Eyebrow (pill arriba del titular)" value={heroEyebrow} onChange={setHeroEyebrow} placeholder="La Tienda Limpack · 74 productos" />
          <Input label="Titular (después de la coma queda en teal)" value={heroTitulo} onChange={setHeroTitulo} placeholder="Limpieza, resuelta cada mes." />
          <Textarea label="Subtítulo" value={heroSubtitulo} onChange={setHeroSubtitulo} placeholder="Elegí tu kit o comprá productos sueltos…" rows={2} />
          <Input label="Texto del botón CTA" value={heroCta} onChange={setHeroCta} placeholder="Ver los kits" />
        </Section>

        {/* Promo bar */}
        <Section titulo="Promo bar (mensajes rotativos)" sub="Mensajes cortos que rotan en la franja superior. Hasta 5.">
          {promoBar.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={m.visible !== false}
                onChange={e => actualizarPromo(i, 'visible', e.target.checked)}
                title="Visible"
              />
              <input
                type="text"
                value={m.texto || ''}
                onChange={e => actualizarPromo(i, 'texto', e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: `1px solid ${LINE}`, fontSize: 13 }}
                placeholder="Envío gratis en tu barrio…"
              />
              <button type="button" onClick={() => quitarPromo(i)} style={btnGhost}>×</button>
            </div>
          ))}
          {promoBar.length < 5 && (
            <button type="button" onClick={agregarPromo} style={btnGhost}>+ Agregar mensaje</button>
          )}
        </Section>

        {/* Categorías */}
        <Section titulo="Categorías del catálogo" sub="Cuáles mostrar, en qué orden, cuántos productos por categoría (preview en la tienda). El total de productos por categoría en DB se muestra al lado.">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                  <th style={th}>Orden</th>
                  <th style={th}>Visible</th>
                  <th style={th}>Slug DB</th>
                  <th style={th}>Label que se muestra</th>
                  <th style={th}>Cuántos mostrar</th>
                  <th style={th}>En DB</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {categoriasConfig.map((c, i) => {
                  const enDb = categoriasDisponibles.find(d => d.slug === c.slug)
                  return (
                    <tr key={c.slug + i} style={{ borderTop: `1px solid ${LINE}` }}>
                      <td style={td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <button type="button" onClick={() => moverCategoria(i, -1)} style={btnTiny} disabled={i === 0}>▲</button>
                          <span style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>{i + 1}</span>
                          <button type="button" onClick={() => moverCategoria(i, +1)} style={btnTiny} disabled={i === categoriasConfig.length - 1}>▼</button>
                        </div>
                      </td>
                      <td style={td}>
                        <input type="checkbox" checked={c.visible !== false} onChange={e => actualizarCategoria(i, 'visible', e.target.checked)} />
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace', color: '#64748b' }}>{c.slug}</td>
                      <td style={td}>
                        <input
                          type="text"
                          value={c.label || ''}
                          onChange={e => actualizarCategoria(i, 'label', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${LINE}`, fontSize: 13 }}
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          value={c.count || 6}
                          onChange={e => actualizarCategoria(i, 'count', Math.max(1, Number(e.target.value) || 6))}
                          style={{ width: 64, padding: '6px 8px', borderRadius: 6, border: `1px solid ${LINE}`, fontSize: 13 }}
                          min={1} max={50}
                        />
                      </td>
                      <td style={{ ...td, color: enDb ? '#047857' : '#DC2626', fontWeight: 600 }}>
                        {enDb ? `${enDb.productos_en_db} prod.` : 'no existe'}
                      </td>
                      <td style={td}>
                        <button type="button" onClick={() => quitarCategoria(i)} style={btnGhost}>Quitar</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {faltantes.length > 0 && (
            <div style={{ marginTop: 12, padding: 10, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, fontSize: 12 }}>
              <strong style={{ color: '#92400E' }}>Categorías en DB que NO están configuradas (clickeá para agregar):</strong>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {faltantes.map(f => (
                  <button key={f.slug} type="button" onClick={() => agregarCategoriaFaltante(f.slug)} style={{
                    background: '#fff', border: '1px dashed #92400E', color: '#92400E',
                    padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>+ {f.slug} ({f.productos_en_db})</button>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Kits tier default */}
        <Section titulo="Toggle kits — tier por default" sub="Cuál se ve seleccionado primero al abrir la tienda.">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="radio" name="tier" checked={tierDefault === 'essential'} onChange={() => setTierDefault('essential')} /> ✦ Essential
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="radio" name="tier" checked={tierDefault === 'premium'} onChange={() => setTierDefault('premium')} /> ★ Premium
            </label>
          </div>
        </Section>

        {/* Empresas */}
        <Section titulo="Bloque empresas (B2B)" sub="Sección final navy con CTA presupuesto.">
          <Input label="Titular (palabra 'empresa' queda en teal)" value={empTitulo} onChange={setEmpTitulo} placeholder="¿Tu empresa también necesita?" />
          <Textarea label="Subtítulo" value={empSubtitulo} onChange={setEmpSubtitulo} rows={2} />
          <Input label="Texto del botón CTA" value={empCta} onChange={setEmpCta} placeholder="Pedir presupuesto" />
          <Input label="Línea de meta debajo del CTA" value={empMeta} onChange={setEmpMeta} placeholder="12 empresas · transferencia 30 días · mínimo $3.000/mes" />
        </Section>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={guardar}
            disabled={saving}
            style={{
              background: saving ? '#94a3b8' : NAVY,
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '14px 28px', fontSize: 14, fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >{saving ? '⏳ Guardando…' : '💾 Guardar todos los cambios'}</button>
        </div>
      </div>
    </AdminLayout>
  )
}

/* ───── Subcomponentes ───── */

function Section({ titulo, sub, children }) {
  return (
    <section style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: NAVY, margin: 0, marginBottom: 4 }}>{titulo}</h2>
      {sub && <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px' }}>{sub}</p>}
      {children}
    </section>
  )
}
function Input({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${LINE}`, fontSize: 14 }}
      />
    </div>
  )
}
function Textarea({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</label>
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${LINE}`, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
      />
    </div>
  )
}

const th = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }
const td = { padding: '8px 10px', verticalAlign: 'middle' }
const btnGhost = {
  background: '#fff', border: `1px solid ${LINE}`, color: '#64748b',
  padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
}
const btnTiny = {
  background: 'transparent', border: 'none', color: '#94a3b8',
  cursor: 'pointer', fontSize: 11, padding: 2,
}
